import { invariant, SUPPLIERS_TYPES_IDS } from '@wyz/utils';
import {
  IAnonymizedSupplierPlatform,
  ICriteriaList,
  ICriteriaUsageVariable,
  ISupplierTemplate,
  ISupplierTemplateForm,
  ISupplierTemplatePayload,
  TabItemType,
} from '~/connect/types';
import { IBrand } from '~/common/types';
import { SUPPLIERS_STATUSES } from '~/connect/schemas';

const ALL_TAB = -1;
export const SUPPLIER_TEMPLATE_TABS: Array<TabItemType> = [
  {
    value: 'ALL',
    label: 'app.common.all_male',
    groupFilterValue: ALL_TAB,
  },
  {
    value: 'MANUFACTURER',
    label: 'app.supplier_types.manufacturer',
    groupFilterValue: SUPPLIERS_TYPES_IDS.MANUFACTURER,
  },
  {
    value: 'WHOLESALER',
    label: 'app.supplier_types.wholesaler',
    groupFilterValue: SUPPLIERS_TYPES_IDS.WHOLESALER,
  },
  {
    value: 'DISTRIBUTOR',
    label: 'app.supplier_template_tab.central_stocks',
    groupFilterValue: SUPPLIERS_TYPES_IDS.PLATFORM_DISTRIBUTOR,
  },
  {
    value: 'WYZ_TROUBLESHOOTING',
    label: 'app.supplier_types.wyz_distributor',
    groupFilterValue: SUPPLIERS_TYPES_IDS.WYZ_DISTRIBUTOR,
    filterCallback({ supplierPlatform }) {
      return (
        supplierPlatform.wyzOffer &&
        supplierPlatform.supplier.supplierType.id ===
          SUPPLIERS_TYPES_IDS.WYZ_DISTRIBUTOR
      );
    },
  },
  {
    value: 'OTHER',
    label: 'app.supplier_template_tab.advanced',
    groupFilterValue: SUPPLIERS_TYPES_IDS.BEST_SOURCING,
  },
];
const getCriterionValuesByIds = (
  criterionId: string,
  criterionValueIds: Array<string>,
  allCriteria: ICriteriaList['data'],
): ISupplierTemplatePayload['criteria'][number]['values'] => {
  const criterion = allCriteria.find((item) => item.id === criterionId);
  invariant(criterion);
  return criterion.values.filter((criterionValue) =>
    criterionValueIds.includes(criterionValue.id),
  );
};

export const rawToForm = (
  supplierTemplate: ISupplierTemplate,
): ISupplierTemplateForm => {
  const bestSourcingSupplier = supplierTemplate.suppliers.find(
    (supplier) => supplier.type === SUPPLIERS_TYPES_IDS.BEST_SOURCING,
  );
  const bestSourcingSupplierConfigurations: ISupplierTemplateForm['bestSourcingSupplierConfigurations'] =
    {};
  for (const configuration of bestSourcingSupplier?.bestSourcingConfigurations ??
    []) {
    for (const supplier of configuration.suppliers) {
      const key = `supplier${supplier.id}`;
      if (!bestSourcingSupplierConfigurations[key]) {
        bestSourcingSupplierConfigurations[key] = {
          orderable: supplier.orderable,
          status: supplier.status,
          enabled: supplier.status === SUPPLIERS_STATUSES.ENABLED,
          customName: supplier.customName,
          childCustomerDelivery: supplier.childCustomerDelivery,
          rankingModelId: supplier.rankingModelId,
          extendedManufConfiguration: {
            enabled: supplier.extendedManufConfiguration?.enabled ?? false,
            warehouseId:
              supplier.extendedManufConfiguration?.warehouseId ?? null,
            displayName:
              supplier.extendedManufConfiguration?.displayName ?? null,
          },
        };
      }
    }
  }
  return {
    ...supplierTemplate,
    suppliers: supplierTemplate.suppliers.map((supplier) => {
      if (supplier.type !== SUPPLIERS_TYPES_IDS.BEST_SOURCING) {
        return {
          ...supplier,
          enabled: supplier.status === SUPPLIERS_STATUSES.ENABLED,
          bestSourcingConfigurations: undefined,
        };
      }

      return {
        ...supplier,
        enabled: supplier.status === SUPPLIERS_STATUSES.ENABLED,
        bestSourcingConfigurations:
          supplier.bestSourcingConfigurations?.map((configuration) => ({
            brands: configuration.brands.map((brand) => ({
              label: brand.name,
              value: brand.publicKey,
            })),
            suppliers: configuration.suppliers.map((supplier) => ({
              label: supplier.name,
              value: supplier.id,
              rank: supplier.rank,
            })),
          })) ?? [],
      };
    }),
    criteria: supplierTemplate.criteria.map((associatedCriterion) => ({
      ...associatedCriterion,
      values: associatedCriterion.values.map(
        (associatedCriterionValue) => associatedCriterionValue.id,
      ),
    })),
    bestSourcingSupplierConfigurations,
  };
};
export const formToPayload = (
  formData: ISupplierTemplateForm,
  allCriteria: ICriteriaList['data'],
  brands: IBrand[],
  supplierPlatforms: IAnonymizedSupplierPlatform[],
): ISupplierTemplatePayload => {
  const brandLookup: Record<string, IBrand> = {};
  for (const brand of brands) {
    brandLookup[brand.publicKey] = brand;
  }
  const supplierPlatformLookup: Record<string, IAnonymizedSupplierPlatform> =
    {};
  for (const supplierPlatform of supplierPlatforms) {
    supplierPlatformLookup[supplierPlatform.supplierPlatform.supplier.id] =
      supplierPlatform;
  }

  return {
    ...formData,
    suppliers: formData.suppliers.map((supplier) => {
      const extendedManufConfiguration = {
        enabled: supplier.extendedManufConfiguration?.enabled ?? false,
        warehouseId:
          supplier.extendedManufConfiguration?.warehouseId ?? undefined,
        displayName:
          supplier.extendedManufConfiguration?.displayName ?? undefined,
      };

      if (supplier.type !== SUPPLIERS_TYPES_IDS.BEST_SOURCING) {
        return {
          ...supplier,
          extendedManufConfiguration,
          bestSourcingConfigurations: undefined,
        };
      }

      return {
        ...supplier,
        extendedManufConfiguration,
        bestSourcingConfigurations:
          supplier.bestSourcingConfigurations?.map((configuration) => ({
            brands: configuration.brands.map((configBrand) => {
              const brand = brandLookup[configBrand.value];

              return {
                id: brand.id,
                name: brand.name,
                publicKey: brand.publicKey,
                i18nKey: brand.i18nKey,
              };
            }),
            suppliers: configuration.suppliers.map((bestSourcingSupplier) => {
              const supplierConfiguration =
                formData.bestSourcingSupplierConfigurations?.[
                  `supplier${bestSourcingSupplier.value}`
                ];
              const supplierPlatform =
                supplierPlatformLookup[bestSourcingSupplier.value];

              return {
                id: bestSourcingSupplier.value,
                name: supplierPlatform.supplierPlatform.supplier.name,
                rank: bestSourcingSupplier.rank,
                orderable: Boolean(supplierConfiguration?.orderable),
                status:
                  supplierConfiguration?.status ?? SUPPLIERS_STATUSES.DISABLED,
                overridePriorityIfShorterDelivery: Boolean(
                  supplier.overridePriorityIfShorterDelivery,
                ),
                customName: supplierConfiguration?.customName ?? undefined,
                childCustomerDelivery: Boolean(
                  supplierConfiguration?.childCustomerDelivery,
                ),
                rankingModelId:
                  supplierConfiguration?.rankingModelId ?? undefined,
                type: supplierPlatform.supplierPlatform.supplier.centralStock
                  ? SUPPLIERS_TYPES_IDS.CENTRAL_STOCK
                  : supplierPlatform.supplierPlatform.supplier.supplierType.id,
                extendedManufConfiguration: {
                  enabled:
                    supplierConfiguration?.extendedManufConfiguration
                      ?.enabled ?? false,
                  warehouseId:
                    supplierConfiguration?.extendedManufConfiguration
                      ?.warehouseId ?? undefined,
                  displayName:
                    supplierConfiguration?.extendedManufConfiguration
                      ?.displayName ?? undefined,
                },
              };
            }),
          })) ?? [],
      };
    }),
    criteria: formData.criteria.map((associatedCriterion) => ({
      ...associatedCriterion,
      values: getCriterionValuesByIds(
        associatedCriterion.criterion.id,
        associatedCriterion.values,
        allCriteria,
      ),
    })),
  };
};

export const groupSupplierByLetter = (
  suppliers: Array<IAnonymizedSupplierPlatform>,
  tabs: Array<TabItemType>,
) => {
  const grouped = {} as Record<
    TabItemType['groupFilterValue'],
    Record<string, Array<IAnonymizedSupplierPlatform>>
  >;
  tabs.forEach((tab) => {
    grouped[tab.groupFilterValue] = {};
  });
  suppliers.forEach((anonymized) => {
    const letter = anonymized.supplierPlatform.supplier.name[0].toUpperCase();
    if (!grouped[anonymized.supplierPlatform.supplier.supplierType.id]) {
      // eslint-disable-next-line no-console
      console.error('Unhandled Type', anonymized.supplierPlatform.supplier);
      return;
    }
    // always add to all tab
    if (!grouped[ALL_TAB][letter]) {
      grouped[ALL_TAB][letter] = [];
    }
    grouped[ALL_TAB][letter].push(anonymized);

    // prepare lookup group
    if (
      !grouped[anonymized.supplierPlatform.supplier.supplierType.id][letter]
    ) {
      grouped[anonymized.supplierPlatform.supplier.supplierType.id][letter] =
        [];
    }

    // add by group
    const itemTab = tabs.find(
      (_item) =>
        _item.groupFilterValue ===
        anonymized.supplierPlatform.supplier.supplierType.id,
    );
    if (itemTab?.filterCallback) {
      const shouldAdd = itemTab.filterCallback(anonymized);
      if (shouldAdd) {
        grouped[itemTab.groupFilterValue][letter].push(anonymized);
      }
    } else {
      grouped[anonymized.supplierPlatform.supplier.supplierType.id][
        letter
      ].push(anonymized);
    }
  });
  return grouped;
};

export function fromFormToQuery(
  id: string,
  data: ISupplierTemplateForm,
): ICriteriaUsageVariable {
  return {
    id: id,
    query: {
      criteria: data.criteria.map(({ criterion, values }) => ({
        criterion: criterion.id,
        values: values,
      })),
    },
  };
}
